/**
 * React hooks for subscribing to the Ghostwriter external store.
 *
 * Uses `useSyncExternalStore` so any component in any React tree can
 * subscribe to the same shared state. No provider wrapper is required.
 */

import { useSyncExternalStore } from "react";

import type { ScheduleApplyConsentStatusResponse } from "../models/ScheduleApplyConsentStatus";
import type { ScheduleApplyJob } from "../models/ScheduleApplyJob";
import type { AsyncState } from "./AsyncState";
import {
  type DraftFieldValue,
  type DraftState,
  subscribe,
  getSnapshot,
  getServerSnapshot,
  setJobsState,
  setConsentStatusState,
  updateJob,
  beginDraft,
  updateDraftField,
  getDraftFieldUpdates,
  discardDraft,
  validateDraftField,
  validateAllDraftFields,
  resetAttemptedFields,
  setAutoAppliableJobKeys,
  addGroupInstance,
  removeGroupInstance,
} from "./GhostwriterStore";

export interface UseGhostwriterResult {
  autoAppliableJobKeys: AsyncState<Set<string>>;
  jobs: AsyncState<ScheduleApplyJob[]>;
  consentStatus: AsyncState<ScheduleApplyConsentStatusResponse>;
  draft: DraftState | null;

  setAutoAppliableJobKeys: (state: AsyncState<Set<string>>) => void;
  setJobsState: (state: AsyncState<ScheduleApplyJob[]>) => void;
  setConsentStatusState: (state: AsyncState<ScheduleApplyConsentStatusResponse>) => void;
  updateJob: (applicationId: string, updates: Partial<ScheduleApplyJob>) => void;

  beginDraft: (applicationId: string) => void;
  updateDraftField: (fieldId: string, value: DraftFieldValue) => void;
  getDraftFieldUpdates: () => {
    applicationId: string;
    fieldUpdates: ReadonlyMap<string, DraftFieldValue>;
  } | null;
  discardDraft: () => void;
}

/**
 * Subscribe to the full Ghostwriter store. Re-renders when any part
 * of the store changes. For narrower subscriptions, use the
 * dedicated selector hooks.
 */
export function useGhostwriter(): UseGhostwriterResult {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return {
    ...state,
    setAutoAppliableJobKeys,
    setJobsState,
    setConsentStatusState,
    updateJob,
    beginDraft,
    updateDraftField,
    getDraftFieldUpdates,
    discardDraft,
  };
}

const getIsGhostwriterAsyncAutoApplySnapshot = () => getSnapshot().isGhostwriterAsyncAutoApply;
const getIsGhostwriterSyncPrefillSnapshot = () => getSnapshot().isGhostwriterSyncPrefill;
const getIsGhostwriterDeprecatedSnapshot = () => getSnapshot().isGhostwriterDeprecated;
const getIsGhostwriterHybridAutomationSnapshot = () => getSnapshot().isGhostwriterHybridAutomation;
const getIsGhostwriterSyncAutoApplySnapshot = () => getSnapshot().isGhostwriterSyncAutoApply;
const getEligibilityAllowlistJobKeysSnapshot = () => getSnapshot().eligibilityAllowlistJobKeys;
const getAutoAppliableJobKeysSnapshot = () => getSnapshot().autoAppliableJobKeys;
const getJobsSnapshot = () => getSnapshot().jobs;
const getConsentStatusSnapshot = () => getSnapshot().consentStatus;
const getDraftSnapshot = () => getSnapshot().draft;

const getIsGhostwriterAsyncAutoApplyServerSnapshot = () =>
  getServerSnapshot().isGhostwriterAsyncAutoApply;
const getIsGhostwriterSyncPrefillServerSnapshot = () =>
  getServerSnapshot().isGhostwriterSyncPrefill;
const getIsGhostwriterDeprecatedServerSnapshot = () => getServerSnapshot().isGhostwriterDeprecated;
const getIsGhostwriterHybridAutomationServerSnapshot = () =>
  getServerSnapshot().isGhostwriterHybridAutomation;
const getIsGhostwriterSyncAutoApplyServerSnapshot = () =>
  getServerSnapshot().isGhostwriterSyncAutoApply;
const getEligibilityAllowlistJobKeysServerSnapshot = () =>
  getServerSnapshot().eligibilityAllowlistJobKeys;
const getAutoAppliableJobKeysServerSnapshot = () => getServerSnapshot().autoAppliableJobKeys;
const getJobsServerSnapshot = () => getServerSnapshot().jobs;
const getConsentStatusServerSnapshot = () => getServerSnapshot().consentStatus;
const getDraftServerSnapshot = () => getServerSnapshot().draft;

/**
 * Subscribe to only the isGhostwriterAsyncAutoApply flag. Returns `null`
 * while the proctor check is still resolving, `true` if the user
 * is in the async_auto_apply_submit bucket, `false` otherwise.
 */
export function useIsGhostwriterAsyncAutoApply(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterAsyncAutoApplySnapshot,
    getIsGhostwriterAsyncAutoApplyServerSnapshot,
  );
}

/**
 * Subscribe to only the isGhostwriterSyncPrefill flag. Returns `null`
 * while the proctor check is still resolving, `true` if the user
 * is in the sync_prefill_submit (synchronous flow) bucket, `false` otherwise.
 */
export function useIsGhostwriterSyncPrefill(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterSyncPrefillSnapshot,
    getIsGhostwriterSyncPrefillServerSnapshot,
  );
}

/**
 * Subscribe to only the isGhostwriterDeprecated flag. Returns `null`
 * while the proctor check is still resolving, `true` if the user
 * is in the deprecated (skip-eligibility) bucket, `false` otherwise.
 */
export function useIsGhostwriterDeprecated(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterDeprecatedSnapshot,
    getIsGhostwriterDeprecatedServerSnapshot,
  );
}

/**
 * Subscribe to only the isGhostwriterHybridAutomation flag. Returns `null` while
 * the proctor check is still resolving, `true` if the user is in the
 * hybrid_automation bucket (sync_prefill_submit's Prefill synchronous flow PLUS the CAPTCHA /
 * 2FA intervention screen), `false` otherwise. See RN-3367.
 */
export function useIsGhostwriterHybridAutomation(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterHybridAutomationSnapshot,
    getIsGhostwriterHybridAutomationServerSnapshot,
  );
}

/**
 * `true` when the user should get sync_prefill_submit's "Prefill" synchronous flow
 * and copy. sync_prefill_submit is the original; hybrid_automation is an exact clone that adds
 * the intervention screen, so it inherits every sync_prefill_submit UI/copy/routing
 * decision. Prefer this over `useIsGhostwriterSyncPrefill() === true` for any
 * Prefill-experience branch; gate the intervention path itself on
 * `useIsGhostwriterHybridAutomation()` instead. Treats the still-resolving `null`
 * state as `false` so a slow allocation never briefly shows Prefill copy.
 */
export function useGhostwriterUsesPrefillExperience(): boolean {
  const isPhaseA = useIsGhostwriterSyncPrefill();
  const isPhaseD = useIsGhostwriterHybridAutomation();
  return isPhaseA === true || isPhaseD === true;
}

/**
 * Subscribe to only the isGhostwriterSyncAutoApply flag. `null` while resolving,
 * `true` if the user is in the sync_auto_apply bucket, `false` otherwise.
 */
export function useIsGhostwriterSyncAutoApply(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterSyncAutoApplySnapshot,
    getIsGhostwriterSyncAutoApplyServerSnapshot,
  );
}

/**
 * Subscribe to only the jobs slice. Does not re-render when
 * consent status or draft changes.
 */
export function useGhostwriterJobs(): AsyncState<ScheduleApplyJob[]> {
  return useSyncExternalStore(subscribe, getJobsSnapshot, getJobsServerSnapshot);
}

/**
 * Subscribe to only the consent status slice. Does not re-render
 * when jobs or draft change.
 */
export function useGhostwriterConsentStatus(): AsyncState<ScheduleApplyConsentStatusResponse> {
  return useSyncExternalStore(subscribe, getConsentStatusSnapshot, getConsentStatusServerSnapshot);
}

export interface UseGhostwriterDraftResult {
  draft: DraftState | null;

  beginDraft: (applicationId: string) => void;
  updateDraftField: (fieldId: string, value: DraftFieldValue) => void;
  getDraftFieldUpdates: () => {
    applicationId: string;
    fieldUpdates: ReadonlyMap<string, DraftFieldValue>;
  } | null;
  discardDraft: () => void;
  validateDraftField: (fieldId: string) => boolean;
  validateAllDraftFields: () => boolean;
  resetAttemptedFields: () => void;
  addGroupInstance: (groupId: string) => string | null;
  removeGroupInstance: (groupId: string, instanceId: string) => void;
}

/**
 * Subscribe to only the draft slice. Does not re-render when
 * jobs or consent status change. Provides draft lifecycle
 * mutations alongside the current draft state.
 */
export function useGhostwriterDraft(): UseGhostwriterDraftResult {
  const draft = useSyncExternalStore(subscribe, getDraftSnapshot, getDraftServerSnapshot);

  return {
    draft,
    beginDraft,
    updateDraftField,
    getDraftFieldUpdates,
    discardDraft,
    validateDraftField,
    validateAllDraftFields,
    resetAttemptedFields,
    addGroupInstance,
    removeGroupInstance,
  };
}

// ---------------------------------------------------------------------------
// Auto-appliable job keys selector hooks
// ---------------------------------------------------------------------------

/**
 * Subscribe to only the autoAppliableJobKeys slice.
 */
export function useAutoAppliableJobKeys(): AsyncState<Set<string>> {
  return useSyncExternalStore(
    subscribe,
    getAutoAppliableJobKeysSnapshot,
    getAutoAppliableJobKeysServerSnapshot,
  );
}

/**
 * Derived hook that checks whether a specific jobKey is in the
 * auto-appliable set. Also exposes `hydrated` to indicate whether
 * Homepage has populated the set at least once.
 */
export function useIsJobAutoAppliable(jobKey: string | null): {
  isAutoAppliable: boolean;
  loading: boolean;
  hydrated: boolean;
} {
  const { data, loading } = useAutoAppliableJobKeys();
  const hydrated = data !== null;
  const isAutoAppliable = jobKey !== null && (data?.has(jobKey) ?? false);
  return { isAutoAppliable, loading, hydrated };
}

/** Subscribe to only the sync_auto_apply allowlist (candidate) set. */
export function useEligibilityAllowlistJobKeys(): Set<string> {
  return useSyncExternalStore(
    subscribe,
    getEligibilityAllowlistJobKeysSnapshot,
    getEligibilityAllowlistJobKeysServerSnapshot,
  );
}

/**
 * Derived hook: whether a jobKey is in the sync_auto_apply allowlist — gates the
 * ViewJob real-time eligibility read.
 */
export function useIsJobInEligibilityAllowlist(jobKey: string | null): boolean {
  const jobKeys = useEligibilityAllowlistJobKeys();
  return jobKey !== null && jobKeys.has(jobKey);
}

/**
 * Finds a single job by jobKey from the shared jobs slice.
 */
export function useGhostwriterJobByKey(jobKey: string | null): {
  job: ScheduleApplyJob | null;
  loading: boolean;
  error: Error | null;
} {
  const jobs = useGhostwriterJobs();
  const job = jobKey ? (jobs.data?.find((j) => j.jobKey === jobKey) ?? null) : null;
  return { job, loading: jobs.loading, error: jobs.error };
}
